import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { Fragment, useState } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import {
  Position,
  ScoreStatus,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { fadeIn } from '@sorare/core/src/atoms/animations/keyframes';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandXXS, LabelS, LabelXS } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import {
  PopoverArrow,
  PopoverContent,
  PopoverPortal,
  PopoverRoot,
  PopoverTrigger,
} from '@sorare/core/src/atoms/ui/Popover';
import { CornerBadge } from '@sorare/core/src/components/CornerBadge';
import { Cost } from '@sorare/core/src/components/player/Cost';
import { PoweredByRotowire } from '@sorare/core/src/components/rotowire/PoweredByRotowire';
import { Gain } from '@sorare/core/src/components/scoring/Gain';
import { Score } from '@sorare/core/src/components/scoring/Score';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import { teamPictureUrl } from '@sorare/core/src/lib/so5';
import { regularMaterial } from '@sorare/core/src/style/utils';

import type {
  PlayerRotowireStatsProjection_AnyPlayerInterface,
  PlayerRotowireStatsProjection_PlayerGameScoreInterface,
} from './__generated__/index.graphql';
import rotowireLogo from './assets/rotowire_mini.svg';

const CustomChip = styled(Horizontal).attrs({ gap: 0.5 })`
  padding: 2px var(--unit);
  border-radius: 100px;
  background-color: var(--prediction-color);
  color: var(--c-white);
`;
const StyledLabelS = styled(BrandXXS)`
  font-variant-numeric: tabular-nums;
`;

const TooltipRoot = styled(Horizontal)`
  ${regularMaterial}
  border-radius: var(--unit);
  padding: var(--intermediate-unit) var(--double-unit);
  background-color: var(--c-nl-600);
`;
const StyledPopoverContent = styled(PopoverContent)`
  transform-origin: var(--radix-popover-content-transform-origin);
  animation: ${fadeIn} 0.2s ease-in;
`;
const StyledPopoverArrow = styled(PopoverArrow)`
  polygon {
    fill: var(--c-nd-150);
  }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1;
  background-color: rgba(var(--c-rgb-black), 0.7);
  animation: ${fadeIn} 0.2s ease-in;
`;

const RotowireLogo = styled.img.attrs({ src: rotowireLogo })``;
const RotowireLogoSmall = styled(RotowireLogo)`
  width: var(--double-unit);
  height: var(--unit);
`;
const ScoreText = styled.text`
  font: var(--t-brand-xxs);
  font-weight: var(--t-bold);
`;

const PlusSign = styled.span`
  font: var(--t-brand-xxs);
  font-weight: var(--t-bold);
  align-self: flex-start;
  line-height: 32px;
  color: var(--c-nd-600);
`;

const Img = styled.img`
  object-fit: contain;
`;

const GameProjectionVertical = styled(Vertical)`
  align-self: flex-start;
`;

type Props = {
  player: PlayerRotowireStatsProjection_AnyPlayerInterface;
  positions: Position[];
  playerGameScores?: (PlayerRotowireStatsProjection_PlayerGameScoreInterface | null)[];
  variant: 'chip' | 'cornerBadge' | 'text' | 'hexagon';
};
export const PlayerRotowireStatsProjection = ({
  player,
  positions,
  playerGameScores = [],
  variant,
}: Props) => {
  const isLaptopAndAbove = useIsLaptop();
  const [open, setOpen] = useState(false);

  if (player.sport === Sport.FOOTBALL) {
    return null;
  }

  const pickMaxScore = player.sport === Sport.NBA;

  // Calculate scores for each position and find best position
  const {
    bestPosition,
    finalScore: projectedScore,
    gainValue,
  } = positions.reduce(
    (acc, position) => {
      const positionScores = playerGameScores.filter(
        pGS => pGS?.positionTyped === position
      );
      const maxScore =
        positionScores.length > 0
          ? Math.floor(
              Math.max(...positionScores.map(pGS => pGS?.projectedScore ?? 0))
            )
          : 0;
      const sumScores = positionScores.reduce(
        (sum, pGS) => sum + Math.floor(pGS?.projectedScore ?? 0),
        0
      );

      const finalScore = pickMaxScore ? maxScore : sumScores;
      const gain =
        isType(player, 'NBAPlayer') && player.lastTenPlayedSo5AverageScore
          ? Math.round(finalScore - player.lastTenPlayedSo5AverageScore)
          : undefined;
      if (finalScore > acc.finalScore) {
        acc.bestPosition = position;
        acc.finalScore = finalScore;
        acc.gainValue = gain;
      }

      acc.scores[position] = finalScore;
      return acc;
    },
    {
      bestPosition: positions[0],
      finalScore: 0,
      gainValue: undefined as number | undefined,
      scores: {} as Record<string, number>,
    }
  );

  const playerGameScoresFiltered = playerGameScores.filter(
    pGS => pGS?.positionTyped === bestPosition
  );

  const color = '#122335';
  const textContent = <FormattedNumber value={projectedScore} />;

  if (variant === 'text') {
    return (
      <Score
        score={projectedScore}
        scoreStatus={ScoreStatus.FINAL}
        shape="TEXT"
      />
    );
  }

  const content = (() => {
    switch (variant) {
      case 'hexagon':
        return (
          <Cost
            size={4}
            color="var(--c-rotowire)"
            score={
              <Vertical gap={0} center>
                <RotowireLogoSmall />
                <StyledLabelS bold color="var(--c-white)">
                  {textContent}
                </StyledLabelS>
              </Vertical>
            }
          />
        );
      case 'chip':
        return (
          <CustomChip style={{ '--prediction-color': color } as CSSProperties}>
            <RotowireLogo />
            <StyledLabelS as="p">{textContent}</StyledLabelS>
            {!!gainValue && (
              <Gain
                value={gainValue}
                size={2}
                strokeColor="var(--c-rotowire)"
              />
            )}
          </CustomChip>
        );
      case 'cornerBadge':
      default:
        return (
          <CornerBadge color={color}>
            <RotowireLogo />
            <StyledLabelS
              as="p"
              color={projectedScore > 0 ? 'var(--c-white)' : 'var(--c-nd-500)'}
            >
              {textContent}
            </StyledLabelS>
            {!!gainValue && (
              <Gain
                value={gainValue}
                size={2}
                strokeColor="var(--c-rotowire)"
              />
            )}
          </CornerBadge>
        );
    }
  })();

  const hoverProps = isLaptopAndAbove
    ? {
        onMouseEnter: () => setOpen(true),
        onMouseLeave: () => setOpen(false),
      }
    : {};

  return (
    <>
      {!!open && !isLaptopAndAbove && <Overlay />}
      <PopoverRoot open={open} onOpenChange={setOpen}>
        <PopoverTrigger {...hoverProps}>{content}</PopoverTrigger>
        <PopoverPortal>
          <StyledPopoverContent collisionPadding={8} {...hoverProps}>
            <TooltipRoot gap={4}>
              <Vertical gap={2} center>
                <LabelS color="var(--c-nd-700)">
                  <FormattedMessage
                    id="GameWeekProjection.tooltip"
                    defaultMessage="Game Week Projection"
                  />
                </LabelS>
                <Horizontal gap={pickMaxScore ? 1 : 0}>
                  {playerGameScoresFiltered.map((pGS, i) => {
                    if (!pGS) {
                      return null;
                    }
                    const isLast = i === playerGameScores.length - 1;
                    const pgsProjectedScore = Math.floor(
                      pGS.projectedScore ?? 0
                    );
                    const isBest =
                      pgsProjectedScore === projectedScore &&
                      projectedScore !== 0;

                    const isPlayingHome =
                      pGS.anyGame.homeTeam?.slug ===
                      pGS.anyPlayerGameStats.anyTeam.slug;

                    const notPlayerTeam = isPlayingHome
                      ? pGS.anyGame.awayTeam
                      : pGS.anyGame.homeTeam;

                    const notPlayerTeamPictureUrl = notPlayerTeam
                      ? teamPictureUrl(notPlayerTeam)
                      : null;

                    const isStartingPitcherPosition =
                      bestPosition === Position.baseball_starting_pitcher;
                    const isProbablePitcher =
                      isType(pGS.anyGame, 'GameOfBaseball') &&
                      pGS.anyGame.probablePitchers?.some(
                        pp => pp.slug === player.slug
                      );
                    return (
                      <Fragment key={pGS.id}>
                        <GameProjectionVertical gap={0.5} center>
                          <div>
                            <svg
                              width={32}
                              height={33}
                              viewBox="0 0 32 33"
                              fill="none"
                            >
                              <path
                                d="M12.6994 0.753381C14.4858 -0.251127 16.6866 -0.251127 18.4729 0.753381L27.6994 5.94173C29.4858 6.94623 30.5862 8.80264 30.5862 10.8117V21.1883C30.5862 23.1974 29.4858 25.0538 27.6994 26.0583L18.4729 31.2466C16.6866 32.2511 14.4858 32.2511 12.6994 31.2466L3.47293 26.0583C1.6866 25.0538 0.586182 23.1974 0.586182 21.1883V10.8117C0.586182 8.80264 1.68661 6.94623 3.47293 5.94173L12.6994 0.753381Z"
                                fill="#122335"
                              />
                              <ScoreText
                                x="50%"
                                y="50%"
                                fill={
                                  pGS.projectedScore > 0
                                    ? 'var(--c-white)'
                                    : 'var(--c-nd-500)'
                                }
                                textAnchor="middle"
                                alignmentBaseline="middle"
                              >
                                {Math.floor(pGS.projectedScore ?? 0)}
                              </ScoreText>
                              <path
                                d="M18.2279 1.1892L27.4544 6.37754C29.0872 7.29572 30.0862 8.98777 30.0862 10.8117V21.1883C30.0862 23.0122 29.0872 24.7043 27.4544 25.6225L18.2279 30.8108C16.5937 31.7297 14.5787 31.7297 12.9445 30.8108L3.71801 25.6225C2.08521 24.7043 1.08618 23.0122 1.08618 21.1883V10.8117C1.08618 8.98777 2.08521 7.29572 3.71801 6.37754L12.9445 1.1892C14.5787 0.270267 16.5937 0.270267 18.2279 1.1892Z"
                                stroke="white"
                                strokeOpacity={isBest ? '0.4' : '0.1'}
                              />
                            </svg>
                          </div>
                          <Vertical gap={0}>
                            <Horizontal>
                              <Chip
                                size="small"
                                color={
                                  isProbablePitcher ||
                                  (!isStartingPitcherPosition &&
                                    pGS.projectedScore > 0)
                                    ? 'green'
                                    : 'transparent'
                                }
                              >
                                <Horizontal gap={0}>
                                  <LabelXS as="span">
                                    {isPlayingHome ? (
                                      <FormattedMessage {...fantasy.vs} />
                                    ) : (
                                      '@'
                                    )}
                                  </LabelXS>
                                  {!!notPlayerTeamPictureUrl && (
                                    <Img
                                      src={notPlayerTeamPictureUrl}
                                      alt={notPlayerTeam?.slug}
                                      width={16}
                                      height={16}
                                    />
                                  )}
                                </Horizontal>
                              </Chip>
                            </Horizontal>
                          </Vertical>
                        </GameProjectionVertical>
                        {!isLast && !pickMaxScore && <PlusSign>+</PlusSign>}
                      </Fragment>
                    );
                  })}
                </Horizontal>
                <PoweredByRotowire />
              </Vertical>
            </TooltipRoot>
            <StyledPopoverArrow />
          </StyledPopoverContent>
        </PopoverPortal>
      </PopoverRoot>
    </>
  );
};

PlayerRotowireStatsProjection.fragments = {
  playerGameScoreInterface: gql`
    fragment PlayerRotowireStatsProjection_PlayerGameScoreInterface on PlayerGameScoreInterface {
      id
      projectedScore
      positionTyped
      anyPlayer {
        slug
      }
      anyPlayerGameStats {
        id
        anyTeam {
          slug
        }
      }
      anyGame {
        id
        homeTeam {
          slug
          code
          ...teamPictureUrl_teamInterface
        }
        awayTeam {
          slug
          code
          ...teamPictureUrl_teamInterface
        }
        ... on GameOfBaseball {
          id
          probablePitchers {
            slug
          }
        }
      }
    }
    ${teamPictureUrl.fragments.teamInterface}
  ` as TypedDocumentNode<PlayerRotowireStatsProjection_PlayerGameScoreInterface>,
  anyPlayerInterface: gql`
    fragment PlayerRotowireStatsProjection_AnyPlayerInterface on AnyPlayerInterface {
      slug
      sport
      anyPositions
      ... on NBAPlayer {
        slug
        lastTenPlayedSo5AverageScore: averageScore(
          type: LAST_TEN_PLAYED_SO5_AVERAGE_SCORE
        )
      }
    }
  ` as TypedDocumentNode<PlayerRotowireStatsProjection_AnyPlayerInterface>,
};
